import {
  WSMenuItem,
  WSTable,
  WSTableItem,
  WSTableProps
} from "@wingspanhq/fe-component-library";
import { RecurringInvoice } from "@wingspanhq/wingspan-api-sdk";
import { queriesPayerRows } from "@query";
import uniq from "lodash/uniq";
import React, { useCallback, useMemo } from "react";
import {
  Route,
  useHistory,
  useLocation,
  useRouteMatch
} from "react-router-dom";
import { RouteInvoiceTemplateDetails } from "../../../../Invoices/screens/RouteInvoiceTemplateDetails";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { buildIcon } from "./buildIcon";
import { getColumns } from "./columns";
import { useGetActions } from "./useGetActions";

type TableItem = WSTableItem<RecurringInvoice>;

type Props = {
  recurringInvoices: RecurringInvoice[];
} & Omit<WSTableProps<RecurringInvoice>, "tableData">;

export const TableInvoiceTemplates: React.FC<Props> = ({
  recurringInvoices,
  ...props
}) => {
  const history = useHistory();
  const location = useLocation();
  const getActions = useGetActions();
  const match = useRouteMatch();

  const payerIds = useMemo(
    () =>
      uniq(
        recurringInvoices
          .map(recurringInvoice => recurringInvoice.payerId)
          .filter((payerId): payerId is string => !!payerId)
      ),
    [recurringInvoices]
  );

  const queryPayerRows = queriesPayerRows.searchPayerRows.useQuery(
    {
      filter: { id: { anyOf: payerIds } },
      page: { size: payerIds.length }
    },
    { enabled: payerIds.length > 0 }
  );

  const payerRowById = useMemo(
    () =>
      new Map(
        (queryPayerRows.data?.data ?? []).map(payerRow => [
          payerRow.id,
          payerRow
        ])
      ),
    [queryPayerRows.data]
  );

  const tableData: TableItem[] = recurringInvoices.map(recurringInvoice => ({
    id: recurringInvoice.id ?? "",
    data: recurringInvoice,
    icon: buildIcon(recurringInvoice)
  }));

  const columns = useMemo(() => getColumns({ payerRowById }), [payerRowById]);

  const onView = useCallback(
    (item: TableItem) => {
      history.push({
        pathname: match.path + "/" + item.id,
        search: location.search,
        state: {
          backPath: location.pathname
        }
      });
    },
    [history, location.pathname, location.search, match.path]
  );

  const onViewInNewTab = useCallback(
    (item: TableItem) => {
      openInNewTab(match.path + `/${item.id}${location.search}`);
    },
    [location.search, match.path]
  );

  const rowMenuActions = useCallback(
    (item: TableItem) => {
      const menuItems: WSMenuItem[] = [];

      menuItems.push({
        icon: "search",
        label: "View",
        onClick: () => {
          onView(item);
        }
      });

      menuItems.push(...getActions(item.data));

      return menuItems;
    },
    [getActions, onView]
  );

  return (
    <>
      <Route
        path={match.path + "/:invoiceTemplateId"}
        component={RouteInvoiceTemplateDetails}
      />
      <WSTable<RecurringInvoice>
        showHeader
        tableData={tableData}
        columns={columns}
        onRowClick={onView}
        onRowControlClick={onViewInNewTab}
        onRowMousewheelClick={onViewInNewTab}
        rowMenuActions={rowMenuActions}
        {...props}
      />
    </>
  );
};
